// @ts-check
// Balão das novidades acima da versão no rodapé da gaveta (contrato 08-contrato-novidades.md, seção 6): não modal,
// não rouba o foco e não bloqueia o uso. "Entendi", Escape, clique fora e "Ver todas as novidades" marcam a versão
// como vista. O balão vive num portal no fim do body: a ordem do Tab o encaixa logo depois do link da versão
// (client/src/lib/release-notes-focus-order.js), e o último botão segue para o conteúdo, sem ciclo.
import { html } from 'htm/preact';
import { useEffect, useRef } from 'preact/hooks';
import { Link } from '../../app/router.js';
import { Dropdown } from '../../ui/dropdown.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { markReleaseNotesSeen } from '../../data/release-notes.js';
import { balloonHighlights } from '../../lib/release-notes-display.js';
import { resolveBalloonTabTarget } from '../../lib/release-notes-focus-order.js';
import { releaseNoteAnchorId, releaseNoteTextKey } from '../../../../shared/release-notes/release-notes.js';
import { t } from '../../i18n/index.js';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled])';
// candidatos a receber o Tab na página; quem tem tabindex negativo, está desabilitado, inerte ou invisível sai depois
const PAGE_TABBABLE_SELECTOR = 'a[href], area[href], button, input, select, textarea, iframe, summary, audio[controls], video[controls], '
  + '[contenteditable]:not([contenteditable="false"]), [tabindex]';
// recolher a gaveta troca o balão pelo cartão: não é "clique fora" e não marca a versão como vista
const DRAWER_TOGGLE_SELECTOR = '[data-navigation-drawer-toggle]';

/** @param {Element} element */
function isTabbable(element) {
  if (!(element instanceof HTMLElement) || element.tabIndex < 0 || element.matches(':disabled') || element.closest('[inert]')) return false;
  return typeof element.checkVisibility === 'function' ? element.checkVisibility({ visibilityProperty: true }) : element.getClientRects().length > 0;
}

/**
 * Focáveis da página fora do balão, na ordem do documento.
 * @param {HTMLElement | null} balloon
 */
function pageTabbables(balloon) {
  return /** @type {HTMLElement[]} */ ([...document.querySelectorAll(PAGE_TABBABLE_SELECTOR)]
    .filter((element) => !balloon?.contains(element) && isTabbable(element)));
}

/**
 * @param {{ anchorRef: { current: HTMLElement | null }, versionLinkRef: { current: HTMLElement | null },
 *   announcement: import('../../../../shared/release-notes/release-notes.js').ReleaseNote, titleId: string, listId: string }} props
 */
export function ReleaseNotesBalloon({ anchorRef, versionLinkRef, announcement, titleId, listId }) {
  const contentRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const lastPointerTargetRef = useRef(/** @type {Element | null} */ (null));
  const textKey = releaseNoteTextKey(announcement.version);
  const focusables = () => /** @type {HTMLElement[]} */ ([...(contentRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) ?? [])]);
  const isFocusInside = () => Boolean(contentRef.current?.contains(document.activeElement));

  // Tab da versão entra no balão e o último botão segue para o que vinha depois da versão; Shift+Tab faz o caminho
  // inverso. Fora desses pontos o navegador segue a ordem dele.
  useEffect(() => {
    /** @param {KeyboardEvent} event */
    const handleTab = (event) => {
      if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const target = resolveBalloonTabTarget({
        pageTabbables: pageTabbables(contentRef.current),
        balloonItems: focusables(),
        versionLink: versionLinkRef.current,
        activeElement,
        isBackward: event.shiftKey,
      });
      if (!target) return;
      event.preventDefault();
      target.focus();
    };
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, []);

  // o Dropdown não diz por que fechou: o alvo do último clique, gravado antes dele, separa o recolher da gaveta
  useEffect(() => {
    /** @param {Event} event */
    const rememberPointerTarget = (event) => {
      lastPointerTargetRef.current = event.type === 'mousedown' && event.target instanceof Element ? event.target : null;
    };
    document.addEventListener('mousedown', rememberPointerTarget, true);
    document.addEventListener('keydown', rememberPointerTarget, true);
    return () => {
      document.removeEventListener('mousedown', rememberPointerTarget, true);
      document.removeEventListener('keydown', rememberPointerTarget, true);
    };
  }, []);

  const handleDropdownClose = () => {
    if (lastPointerTargetRef.current?.closest(DRAWER_TOGGLE_SELECTOR)) return;
    markReleaseNotesSeen();
  };

  /** @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    // o Dropdown fecha e marca no Escape; o foco volta para onde o balão está preso
    if (event.key === 'Escape') versionLinkRef.current?.focus();
  };

  const dismiss = () => {
    const shouldReturnFocus = isFocusInside();
    markReleaseNotesSeen();
    if (shouldReturnFocus) versionLinkRef.current?.focus();
  };

  return html`<${Dropdown}
    anchorRef=${anchorRef}
    isOpen=${true}
    onClose=${handleDropdownClose}
    placement="right-end"
    offset=${8}
    width=${300}
    role="dialog"
    ariaModal="false"
    ariaLabelledBy=${titleId}
    ariaDescribedBy=${listId}
    className="release-notes-balloon"
  >
    <div class="release-notes-balloon-content" ref=${contentRef} onKeyDown=${handleKeyDown} data-testid="release-notes-balloon">
      <div class="release-notes-surface-header">
        <span class="release-notes-surface-icon" aria-hidden="true"><${Icon} name="IconSparkles" size=${16} /></span>
        <h2 class="release-notes-surface-title" id=${titleId}>${t('releaseNotes.balloon.title', { version: announcement.version })}</h2>
      </div>
      <ul class="release-notes-balloon-list" id=${listId}>
        ${balloonHighlights(announcement).map((highlight) => html`<li key=${highlight.key}>${t(`releaseNotes.versions.${textKey}.highlights.${highlight.key}.title`)}</li>`)}
      </ul>
      <div class="release-notes-surface-actions">
        <${Link}
          href=${`/release-notes#${releaseNoteAnchorId(announcement.version)}`}
          className="button release-notes-see-all"
          data-variant="secondary"
          data-accent="default"
          data-size="small"
          onClick=${() => markReleaseNotesSeen()}
        ><span class="button-title">${t('releaseNotes.balloon.seeAll')}</span><//>
        <${Button} title=${t('releaseNotes.balloon.dismiss')} accent="blue" size="small" onClick=${dismiss} />
      </div>
    </div>
  <//>`;
}
